import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import api, { getErrorMessage } from '../../services/api';
import { currentMonthKey } from './arBillingDates';
import CompanyArBilling from './CompanyArBilling';
import RecoveryBilling from './RecoveryBilling';
import TariffTab from './TariffTab';

export { currentMonthKey };

/**
 * «Facturación» (docs/facturacion-cliente-plan.md), con dos pestañas:
 *
 *   · AR: los euros gestionados del mes y lo que se factura por tramos. La ve la
 *     empresa con AR activado.
 *   · Recobro: lo cobrado en el mes en sus expedientes de recobro y los
 *     honorarios. La ve la empresa que tiene expedientes en recobro.
 *
 * El admin ve las dos: en AR, el resumen de todos; en Recobro, elige el cliente.
 *
 *   · Precios: la tarifa de la empresa, AR y recobro, en solo lectura. Solo la
 *     empresa; los precios los pone el admin desde el perfil
 *     (docs/precios-en-facturacion-plan.md).
 *
 * La pestaña AR del admin: el resumen de todos los clientes AR en un mes
 * (lo que mira ClaimUp para facturar); una company ve el suyo, con el detalle
 * y el aviso de fin de mes (CompanyArBilling). El resto, fuera.
 *
 * Gestionado en el mes = lo activo el día 1 + lo que entra durante el mes.
 * Plan: docs/ar-historico-facturacion-plan.md y docs/ar-panel-cliente-plan.md.
 */

const eur = n =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);

const monthLabel = key => {
  const [y, m] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, 15))
  );
};

function AdminArSummary() {
  const { user } = userStore();
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    setLoading(true);
    api
      .get('/ar-billing/summary', { params: { month } })
      .then(({ data: res }) => setData(res.data))
      .catch(err => {
        setData(null);
        toast.error(getErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [month, user?.role]);

  return (
    <>
      <div className='space-y-4'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 py-4 flex flex-wrap items-end justify-between gap-3'>
          <div>
            <h2 className='font-semibold text-slate-800'>AR: todos los clientes</h2>
            <p className='text-sm text-slate-500 mt-1'>
              Euros gestionados por cada cliente AR en el mes: lo activo el día 1 más lo que entra durante el
              mes. Pagos y bajas no restan hasta el mes siguiente.
            </p>
          </div>
          <label className='text-sm text-slate-600 flex items-center gap-2'>
            Mes
            <input
              type='month'
              aria-label='Mes'
              value={month}
              max={currentMonthKey()}
              onChange={e => e.target.value && setMonth(e.target.value)}
              className='form-input rounded-lg border-slate-300 text-sm'
            />
          </label>
        </header>

        {loading && <p className='text-sm text-slate-500'>Cargando…</p>}

        {!loading && data && (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-4 gap-4'>
              <Stat label='Arrastrado del día 1' value={eur(data.totals.opening)} />
              <Stat label='Nuevo en el mes' value={eur(data.totals.entries)} />
              <Stat
                label={`Total gestionado · ${monthLabel(data.month)}${data.current ? ' (en curso)' : ''}`}
                value={eur(data.totals.total)}
              />
              <Stat label='A facturar · sin IVA' value={eur(data.totals.amount)} strong />
            </div>
            {data.without_price > 0 && (
              <p className='text-sm text-amber-700'>
                {data.without_price === 1 ? '1 cliente no suma' : `${data.without_price} clientes no suman`} a «a
                facturar»: sin tarifa definida, o por encima de su último tramo. Se definen en el perfil de cada empresa.
              </p>
            )}

            <div className='bg-white border border-slate-200 rounded-lg overflow-x-auto'>
              {data.companies.length === 0 ? (
                <p className='px-5 py-6 text-sm text-slate-500'>No hay ningún cliente con AR activado.</p>
              ) : (
                <table className='min-w-full text-sm'>
                  <thead className='bg-slate-50 text-slate-500 text-xs uppercase'>
                    <tr>
                      <th className='text-left px-4 py-2 font-semibold'>Cliente</th>
                      <th className='text-right px-4 py-2 font-semibold'>Arrastrado del día 1</th>
                      <th className='text-right px-4 py-2 font-semibold'>Nuevo en el mes</th>
                      <th className='text-right px-4 py-2 font-semibold'>Total gestionado</th>
                      <th className='text-right px-4 py-2 font-semibold'>A facturar</th>
                      <th className='text-left px-4 py-2 font-semibold'>Estado</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-slate-100'>
                    {data.companies.map(c => (
                      <tr key={c.company_id}>
                        <td className='px-4 py-2 text-slate-800'>{c.name}</td>
                        <td className='px-4 py-2 text-right tabular-nums'>
                          {eur(c.opening)}
                          <span className='text-slate-400 text-xs ml-1'>({c.opening_count})</span>
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums'>
                          {eur(c.entries)}
                          <span className='text-slate-400 text-xs ml-1'>({c.entries_count})</span>
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums font-semibold text-slate-800'>
                          {eur(c.total)}
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums'>
                          <AFacturar billing={c.billing} />
                        </td>
                        <td className='px-4 py-2'>
                          <EstadoMes closed={c.closed} current={data.current} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className='bg-slate-50 font-semibold text-slate-800'>
                    <tr>
                      <td className='px-4 py-2'>Total</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{eur(data.totals.opening)}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{eur(data.totals.entries)}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{eur(data.totals.total)}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{eur(data.totals.amount)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
            <p className='text-xs text-slate-400'>
              Entre paréntesis, el número de expedientes. Un mes cerrado ya no cambia; uno en curso o sin cerrar se
              calcula al momento.
            </p>
          </>
        )}
      </div>
    </>
  );
}

const TABS = [
  { id: 'ar', label: 'AR' },
  { id: 'recovery', label: 'Recobro' },
  { id: 'prices', label: 'Precios' },
];

function ArBillingPage() {
  const { user } = userStore();
  const esAdmin = user?.role === 'admin';
  // La empresa, o alguien de su equipo si la empresa se lo ha encendido
  // (docs/facturacion-equipo-plan.md): entonces ve solo sus expedientes, y la
  // empresa de la que se habla es la suya (`parent_id`).
  const esEquipo = ['operator', 'gestor'].includes(user?.role);
  const esEmpresa = user?.role === 'company' || esEquipo;
  const companyId = esEquipo ? user?.parent_id : user?.id;
  // Qué pestañas tiene: el admin las dos; la empresa, lo que diga el servidor.
  const [disponible, setDisponible] = useState(esAdmin ? { ar: true, recovery: true } : null);
  const [tab, setTab] = useState(esAdmin ? 'ar' : null);
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState('');

  useEffect(() => {
    if (!esEmpresa) return;
    api
      .get(`/recovery-billing/companies/${companyId}/available`)
      .then(({ data: res }) => {
        // «Precios» solo la empresa: su equipo ve la facturación de sus
        // expedientes, no las condiciones del contrato.
        const conPrecios = { ...res.data, prices: user?.role === 'company' };
        setDisponible(conPrecios);
        setTab(res.data.ar ? 'ar' : res.data.recovery ? 'recovery' : conPrecios.prices ? 'prices' : null);
      })
      .catch(err => toast.error(getErrorMessage(err)));
  }, [esEmpresa, companyId, user?.role]);

  useEffect(() => {
    if (!esAdmin) return;
    api
      .get('/recovery-billing/companies')
      .then(({ data: res }) => setEmpresas(res.data || []))
      .catch(err => toast.error(getErrorMessage(err)));
  }, [esAdmin]);

  if (!esAdmin && !esEmpresa) return <Navigate to='/dashboard' replace />;

  const tabs = disponible ? TABS.filter(t => disponible[t.id]) : [];

  return (
    <MainLayout>
      <div className='space-y-4'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
          <h1 className='text-2xl font-semibold text-slate-900'>Facturación</h1>
          <p className='text-sm text-slate-500 mt-1'>
            {esAdmin
              ? 'Lo que se factura a cada cliente: AR por tramos de euros gestionados, recobro por lo cobrado.'
              : esEquipo
              ? 'La facturación de los expedientes que tienes asignados: AR por los euros en gestión, recobro por lo que se cobra.'
              : 'Lo que os facturamos cada mes: AR por los euros que tenéis en gestión, recobro por lo que se cobra.'}
          </p>
        </header>

        {disponible && tabs.length === 0 && (
          <p className='bg-white border border-slate-200 rounded-lg px-5 py-6 text-sm text-slate-500'>
            Todavía no hay nada que facturar: ni AR activado ni expedientes en recobro.
          </p>
        )}

        {tabs.length > 1 && (
          <div className='inline-flex rounded-lg border border-slate-200 bg-white p-1' role='tablist'>
            {tabs.map(t => (
              <button
                key={t.id}
                type='button'
                role='tab'
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  tab === t.id ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {tab === 'prices' && <TariffTab companyId={companyId} />}

        {tab === 'ar' && (esAdmin ? <AdminArSummary /> : <CompanyArBilling companyId={companyId} />)}

        {tab === 'recovery' &&
          (esAdmin ? (
            <div className='space-y-4'>
              <select
                aria-label='Cliente'
                value={empresa}
                onChange={e => setEmpresa(e.target.value)}
                className='rounded-lg border-slate-300 text-sm'
              >
                <option value=''>Elige un cliente…</option>
                {empresas.map(c => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {empresa && <RecoveryBilling companyId={empresa} />}
            </div>
          ) : (
            <RecoveryBilling companyId={companyId} />
          ))}
      </div>
    </MainLayout>
  );
}

function Stat({ label, value, strong }) {
  return (
    <div className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
      <div className='text-xs text-slate-500'>{label}</div>
      <div className={`mt-1 tabular-nums ${strong ? 'text-2xl font-semibold text-slate-900' : 'text-xl text-slate-800'}`}>
        {value}
      </div>
    </div>
  );
}

/** Lo que se factura a un cliente, o por qué no hay importe. */
export function AFacturar({ billing }) {
  if (!billing) return <span className='text-xs text-slate-400'>Sin tarifa</span>;
  if (billing.above_last) return <span className='text-xs text-amber-700'>Pasa del último tramo</span>;
  return <span title={`Tramo desde ${eur(billing.from)}`}>{eur(billing.amount)}</span>;
}

function EstadoMes({ closed, current }) {
  if (closed) return <span className='text-xs px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-700'>Cerrado</span>;
  if (current) return <span className='text-xs px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700'>En curso</span>;
  return <span className='text-xs px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700'>Sin cerrar</span>;
}

export default ArBillingPage;
